import { trpc } from "@/lib/trpc";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SeoTags from "@/components/SeoTags";
import MarketProductCard from "@/components/market/MarketProductCard";
import Sparkline from "@/components/market/Sparkline";
import TwoToneHeading from "@/components/market/TwoToneHeading";
import { useProductAnalytics } from "@/hooks/useProductAnalytics";
import { toDisplayAssetUrl } from "@/lib/assets";
import {
  MARKET_NAMES,
  MARKET_SHORTCUTS,
  MOVERS_MIN_PRICE,
  PHOTO_PLATE_CLASS,
  formatChange,
  getChangeColorClass,
  getMarketChangeValue,
  getProductCardSubtitle,
  getReleaseTimestamp,
  getReleaseYear,
  getSalesCount,
  fullDateFormatter,
  median,
  monthYearFormatter,
  type PricePoint,
} from "@/lib/marketDisplay";
import {
  getCatalogFilter,
  getCatalogFilterPath,
  resolveCatalogFilterSlug,
} from "@/lib/routes";
import NotFound from "@/pages/NotFound";
import { getProductPath } from "@shared/productRouting";
import { buildCategorySeo, getCategoryPageContent } from "@shared/seo";
import type { Product } from "@shared/types";
import { Link, useLocation, useParams, useSearch } from "wouter";
import type { CSSProperties } from "react";
import { useState, useMemo, useEffect } from "react";
import { LayoutGrid, List, Search } from "lucide-react";

type SortKey = "newest" | "sold" | "low" | "high" | "gainers" | "losers";

const SORT_TABS: { value: SortKey; label: string }[] = [
  { value: "newest", label: "Newest" },
  { value: "sold", label: "Most sold" },
  { value: "low", label: "Lowest price" },
  { value: "high", label: "Highest price" },
  { value: "gainers", label: "Gainers" },
  { value: "losers", label: "Losers" },
];

// The rest of the models show after "Show all", or as soon as the visitor searches or filters.
const PREVIEW_MODEL_COUNT = 15;
const RELEASE_YEAR_CHIP_COUNT = 4;
const EARLIER_YEARS = "earlier";

const priceFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const exactPriceFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const salesCountFormatter = new Intl.NumberFormat("en-US");

function getVsRetail(product: Product) {
  if (!product.retailPrice || product.currentPrice <= 0) return null;
  return ((product.currentPrice - product.retailPrice) / product.retailPrice) * 100;
}

function compareNewest(a: Product, b: Product) {
  return (
    (getReleaseTimestamp(b) ?? 0) - (getReleaseTimestamp(a) ?? 0) ||
    b.currentPrice - a.currentPrice
  );
}

// Products without a value sort last whichever way the list runs.
function compareNullableNumbers(a: number | null, b: number | null, direction: 1 | -1) {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return (a - b) * direction;
}

function matchesSearch(product: Product, query: string) {
  const haystack = `${product.brand} ${product.name} ${product.marketModel}`.toLowerCase();
  return query
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every(term => haystack.includes(term));
}

// The biggest move in one direction. Cheap models swing on a handful of sales, so they only
// count when no model over the movers' price floor moved that way.
function getBiggestMove(products: Product[], direction: 1 | -1) {
  const moved = products
    .filter(product => (getMarketChangeValue(product) ?? 0) * direction > 0)
    .sort(
      (a, b) =>
        ((getMarketChangeValue(b) ?? 0) - (getMarketChangeValue(a) ?? 0)) * direction
    );

  return moved.find(product => product.currentPrice >= MOVERS_MIN_PRICE) ?? moved[0];
}

function getHighlights(products: Product[]) {
  const newest = [...products]
    .filter(product => getReleaseTimestamp(product) != null)
    .sort(compareNewest)[0];
  const highlights = [
    { key: "gainer", label: "Top gainer", labelClassName: "text-[#1A7F37] dark:text-emerald-400", product: getBiggestMove(products, 1) },
    { key: "loser", label: "Top loser", labelClassName: "text-[#D1242F] dark:text-rose-400", product: getBiggestMove(products, -1) },
    { key: "newest", label: "Newest release", labelClassName: "text-[#B4581A] dark:text-orange-300", product: newest },
  ];

  // In a small market the newest model can also be the top mover; show it once.
  return highlights.filter(
    (highlight, index): highlight is (typeof highlights)[number] & { product: Product } =>
      highlight.product != null &&
      highlights.findIndex(other => other.product?.id === highlight.product?.id) === index
  );
}

function getHighlightNote(product: Product) {
  const released = getReleaseTimestamp(product);
  const parts = [
    product.retailPrice ? `Retail ${priceFormatter.format(product.retailPrice)}` : null,
    released == null ? null : `Released ${fullDateFormatter.format(released)}`,
  ];
  return parts.filter(Boolean).join(" · ");
}

export default function Browse() {
  const params = useParams<{ filter?: string }>();
  const [, navigate] = useLocation();
  const searchString = useSearch();

  const urlSearch = useMemo(
    () => new URLSearchParams(searchString).get("search") || "",
    [searchString]
  );

  const [searchQuery, setSearchQuery] = useState(urlSearch);
  const [sortKey, setSortKey] = useState<SortKey>("newest");
  const [releaseYear, setReleaseYear] = useState<string | null>(null);
  const [showAllModels, setShowAllModels] = useState(false);
  const [view, setView] = useState<"grid" | "list">("grid");
  const { trackProductClick } = useProductAnalytics();

  useEffect(() => {
    setSearchQuery(urlSearch);
  }, [urlSearch]);

  useEffect(() => {
    const canonicalFilterSlug = resolveCatalogFilterSlug(params.filter);
    if (!params.filter || !canonicalFilterSlug || params.filter === canonicalFilterSlug) {
      return;
    }

    const search = searchString ? `?${searchString}` : "";
    navigate(`${getCatalogFilterPath(params.filter)}${search}`, { replace: true });
  }, [navigate, params.filter, searchString]);

  const { data: categories } = trpc.categories.list.useQuery();

  const selectedFilter = useMemo(() => getCatalogFilter(params.filter), [params.filter]);

  // Moving to another market starts its list fresh; the grid or list choice carries over.
  useEffect(() => {
    setSortKey("newest");
    setReleaseYear(null);
    setShowAllModels(false);
  }, [selectedFilter?.slug]);

  const selectedCategory = useMemo(() => {
    if (!selectedFilter?.categorySlug || !categories) return undefined;
    return categories.find((category) => category.slug === selectedFilter.categorySlug);
  }, [categories, selectedFilter?.categorySlug]);

  const hasInvalidFilter = Boolean(params.filter) && !selectedFilter;
  // Only a successful categories response can prove a category is missing; a failed request must not 404 the hub.
  const hasMissingCategory = Boolean(selectedFilter?.categorySlug) && Boolean(categories) && !selectedCategory;

  // The whole market loads once; sorting, searching and filtering happen in the browser.
  const queryInput = useMemo(
    () => ({
      categoryId: selectedCategory?.id,
      brand: selectedFilter?.brand,
      sortBy: "releaseDate",
      sortDir: "desc" as const,
      limit: 100,
      offset: 0,
    }),
    [selectedCategory?.id, selectedFilter?.brand]
  );

  const { data, isLoading } = trpc.products.list.useQuery(queryInput, {
    enabled: Boolean(selectedFilter?.brand) || Boolean(selectedCategory),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const products = useMemo(() => data?.items ?? [], [data?.items]);
  const productIds = useMemo(
    () => products.map(product => product.id).sort((a, b) => a - b),
    [products]
  );
  const { data: recentHistories, isLoading: historiesLoading } =
    trpc.market.recentPriceHistories.useQuery(
      { productIds },
      {
        enabled: productIds.length > 0,
        staleTime: 5 * 60_000,
        refetchOnWindowFocus: false,
      }
    );
  const historiesByProductId = useMemo(
    () =>
      new Map<number, PricePoint[]>(
        (recentHistories ?? []).map(entry => [entry.productId, entry.points])
      ),
    [recentHistories]
  );
  const recentSalesByProductId = useMemo(
    () =>
      new Map(
        (recentHistories ?? []).map(entry => [entry.productId, getSalesCount(entry.points)])
      ),
    [recentHistories]
  );

  // Only the market-shortcut photos come from the full catalog, so it loads after the list.
  const { data: catalog } = trpc.products.all.useQuery(undefined, {
    enabled: Boolean(data),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
  const relatedMarkets = useMemo(() => {
    const catalogBySlug = new Map(
      (catalog ?? [])
        .filter((product): product is Product => product != null)
        .map(product => [product.slug, product])
    );

    return MARKET_SHORTCUTS.filter(shortcut => shortcut.slug !== selectedFilter?.slug).map(
      shortcut => ({
        slug: shortcut.slug,
        title:
          "title" in shortcut
            ? shortcut.title
            : (getCatalogFilter(shortcut.slug)?.label ?? shortcut.slug),
        imageUrl: toDisplayAssetUrl(catalogBySlug.get(shortcut.productSlug)?.imageUrl),
      })
    );
  }, [catalog, selectedFilter?.slug]);

  const marketStats = useMemo(() => {
    const prices = products
      .map(product => product.currentPrice)
      .filter(price => price > 0);
    const changes = products
      .map(getMarketChangeValue)
      .filter((change): change is number => change != null);

    return {
      modelCount: products.length,
      rising: changes.filter(change => change > 0).length,
      falling: changes.filter(change => change < 0).length,
      priceRange:
        prices.length > 0
          ? `${priceFormatter.format(Math.min(...prices))}–${priceFormatter.format(Math.max(...prices))}`
          : "—",
      medianPrice: prices.length > 0 ? priceFormatter.format(median(prices)) : "—",
    };
  }, [products]);
  const highlights = useMemo(() => getHighlights(products), [products]);

  const releaseYearChips = useMemo(() => {
    const years = Array.from(
      new Set(
        products
          .map(getReleaseYear)
          .filter((year): year is number => year != null)
      )
    ).sort((a, b) => b - a);
    const chips = years.slice(0, RELEASE_YEAR_CHIP_COUNT).map(year => ({
      value: String(year),
      label: String(year),
    }));
    if (years.length > RELEASE_YEAR_CHIP_COUNT) {
      chips.push({ value: EARLIER_YEARS, label: "Earlier" });
    }
    return years.length > 1 ? chips : [];
  }, [products]);
  const oldestChipYear = Number(
    releaseYearChips.filter(chip => chip.value !== EARLIER_YEARS).at(-1)?.value
  );

  const filteredProducts = useMemo(() => {
    const query = searchQuery.trim();
    const matching = products.filter(product => {
      if (query && !matchesSearch(product, query)) return false;
      if (!releaseYear) return true;

      const year = getReleaseYear(product);
      return releaseYear === EARLIER_YEARS
        ? year == null || year < oldestChipYear
        : year === Number(releaseYear);
    });

    return matching.sort((a, b) => {
      switch (sortKey) {
        case "sold":
          return (
            compareNullableNumbers(
              recentSalesByProductId.get(a.id) ?? null,
              recentSalesByProductId.get(b.id) ?? null,
              -1
            ) || compareNewest(a, b)
          );
        case "low":
          return compareNullableNumbers(
            a.currentPrice > 0 ? a.currentPrice : null,
            b.currentPrice > 0 ? b.currentPrice : null,
            1
          );
        case "high":
          return b.currentPrice - a.currentPrice;
        case "gainers":
          return compareNullableNumbers(getMarketChangeValue(a), getMarketChangeValue(b), -1);
        case "losers":
          return compareNullableNumbers(getMarketChangeValue(a), getMarketChangeValue(b), 1);
        default:
          return compareNewest(a, b);
      }
    });
  }, [products, searchQuery, releaseYear, oldestChipYear, sortKey, recentSalesByProductId]);

  const isNarrowed = Boolean(searchQuery.trim()) || releaseYear != null;
  const shownProducts =
    showAllModels || isNarrowed
      ? filteredProducts
      : filteredProducts.slice(0, PREVIEW_MODEL_COUNT);

  if (hasInvalidFilter || hasMissingCategory || !selectedFilter) {
    return <NotFound />;
  }

  const categoryTitle = MARKET_NAMES[selectedFilter.slug]?.title ?? selectedFilter.label;
  const categoryName = MARKET_NAMES[selectedFilter.slug]?.name ?? selectedFilter.label;
  const categoryPageContent = getCategoryPageContent(selectedFilter.slug, selectedFilter.label);
  const seoMetadata = buildCategorySeo({
    origin: typeof window === "undefined" ? undefined : window.location.origin,
    filterSlug: selectedFilter.slug,
    filterLabel: selectedFilter.label,
    canonicalPath: `/categories/${selectedFilter.slug}`,
    products: products.slice(0, 12),
  });
  const faqItems = [
    ...categoryPageContent.faq.map(item => ({ question: item.question, answer: item.answer })),
    ...categoryPageContent.searchPrompts.map(prompt => ({
      question: prompt.title,
      answer: prompt.description,
    })),
  ].filter(
    (item, index, items) =>
      items.findIndex(
        other => other.question.toLowerCase() === item.question.toLowerCase()
      ) === index
  );
  const trackClick = (product: Product, source: string) =>
    trackProductClick(product.id, `category-${source}`);

  return (
    <div
      className="min-h-screen flex flex-col bg-background"
      style={{ backgroundColor: "var(--background)" }}
    >
      <SeoTags metadata={seoMetadata} />
      <Navbar />

      <main className="flex-1">
        {/* Header */}
        <section aria-labelledby="category-title" className="container pt-6 md:pt-7">
          <nav aria-label="Breadcrumb" className="text-[0.8rem] text-[#6E6E73] dark:text-zinc-400">
            <Link href="/" className="hover:text-foreground">
              Home
            </Link>
            <span aria-hidden="true" className="mx-2">
              ›
            </span>
            <span aria-current="page" className="text-foreground">
              {categoryTitle}
            </span>
          </nav>
          <div className="mt-4">
            <TwoToneHeading
              id="category-title"
              as="h1"
              title={`${categoryTitle}.`}
              subtitle="What every model is selling for right now."
              sizeClassName="text-[2.125rem] sm:text-[3.25rem]"
            />
          </div>
          <p className="mt-4 max-w-[40rem] text-[0.95rem] leading-7 text-[#424245] dark:text-zinc-300 sm:text-[1.05rem]">
            {categoryPageContent.intro}
          </p>
          <dl className="mt-7 grid grid-cols-2 gap-x-5 gap-y-4 border-t border-[#E8E8ED] pt-5 dark:border-white/10 sm:mt-9 md:grid-cols-5">
            {[
              { label: "Models tracked", value: products.length > 0 ? String(marketStats.modelCount) : "—" },
              { label: "Median price", value: marketStats.medianPrice },
              {
                label: "Rising",
                value: products.length > 0 ? String(marketStats.rising) : "—",
                className: "text-[#1A7F37] dark:text-emerald-400",
              },
              {
                label: "Falling",
                value: products.length > 0 ? String(marketStats.falling) : "—",
                className: "text-[#D1242F] dark:text-rose-400",
              },
              { label: "Price range", value: marketStats.priceRange, wide: true },
            ].map(stat => (
              <div key={stat.label} className={stat.wide ? "col-span-2 md:col-span-1" : ""}>
                <dt className="text-xs text-[#6E6E73] dark:text-zinc-400 sm:text-[0.8rem]">
                  {stat.label}
                </dt>
                <dd
                  className={`mt-1 font-mono text-[1.375rem] font-medium tracking-[-0.02em] sm:mt-1.5 sm:text-[1.625rem] ${
                    stat.className ?? "text-foreground"
                  }`}
                >
                  {stat.value}
                </dd>
              </div>
            ))}
          </dl>
        </section>

        {/* Highlights */}
        {highlights.length > 0 ? (
          <section aria-label="Highlights" className="container pt-8 sm:pt-10">
            <div
              className="scrollbar-none -mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 [touch-action:pan-x] md:mx-0 md:grid md:grid-cols-[repeat(var(--highlight-count),minmax(0,1fr))] md:gap-4 md:px-0"
              style={{ "--highlight-count": highlights.length } as CSSProperties}
            >
              {highlights.map(highlight => {
                const change = getMarketChangeValue(highlight.product);

                return (
                  <Link
                    key={highlight.key}
                    href={getProductPath(highlight.product)}
                    onClick={() => trackClick(highlight.product, `highlight-${highlight.key}`)}
                    className="flex w-[18rem] shrink-0 snap-start items-center gap-3 rounded-[1.125rem] bg-[#F5F5F7] py-4 pl-5 pr-4 text-foreground transition-[box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_32px_-12px_rgba(17,17,19,0.18)] dark:bg-card dark:ring-1 dark:ring-inset dark:ring-white/[0.06] md:w-auto md:min-h-[9.25rem] md:gap-4 md:rounded-[1.25rem] md:pl-6"
                  >
                    <span className="min-w-0 flex-1">
                      <span className={`block text-xs font-medium sm:text-[0.8rem] ${highlight.labelClassName}`}>
                        {highlight.label}
                      </span>
                      <span className="mt-1 block truncate text-[1.05rem] font-semibold tracking-[-0.02em] sm:text-[1.2rem]">
                        {highlight.product.marketModel}
                      </span>
                      <span className="mt-2 flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5">
                        <span className="font-mono text-base font-medium sm:text-lg">
                          {exactPriceFormatter.format(highlight.product.currentPrice)}
                        </span>
                        {change != null && highlight.key !== "newest" ? (
                          <span className={`font-mono text-[0.8rem] font-medium sm:text-sm ${getChangeColorClass(change)}`}>
                            {formatChange(change)}
                          </span>
                        ) : null}
                      </span>
                      <span className="mt-1 hidden truncate text-xs text-[#6E6E73] dark:text-zinc-400 md:block">
                        {getHighlightNote(highlight.product)}
                      </span>
                    </span>
                    <span className="flex h-[5.25rem] w-[5.25rem] shrink-0 items-center justify-center md:h-[6.75rem] md:w-[6.75rem]">
                      {highlight.product.imageUrl ? (
                        <img
                          src={toDisplayAssetUrl(highlight.product.imageUrl)}
                          alt=""
                          className={`max-h-full max-w-full object-contain ${PHOTO_PLATE_CLASS}`}
                        />
                      ) : null}
                    </span>
                  </Link>
                );
              })}
            </div>
          </section>
        ) : null}

        {/* All Models */}
        <section aria-labelledby="category-models" className="container pt-12 md:pt-[4.5rem]">
          <h2
            id="category-models"
            className="text-2xl font-semibold tracking-[-0.02em] text-foreground sm:text-[1.75rem]"
          >
            All {categoryName} models
          </h2>

          <div className="mt-3.5 flex flex-col gap-3.5 md:mt-5 md:flex-row md:items-end md:justify-between md:gap-6 md:border-b md:border-[#E8E8ED] md:dark:border-white/10">
            <div
              role="tablist"
              aria-label="Sort models"
              className="scrollbar-none -mx-4 flex gap-[1.375rem] overflow-x-auto px-4 shadow-[inset_0_-1px_0_#E8E8ED] [touch-action:pan-x] dark:shadow-[inset_0_-1px_0_rgba(255,255,255,0.1)] md:mx-0 md:-mb-px md:gap-[1.625rem] md:px-0"
            >
              {SORT_TABS.map(tab => {
                const isSelected = tab.value === sortKey;

                return (
                  <button
                    key={tab.value}
                    type="button"
                    role="tab"
                    aria-selected={isSelected}
                    onClick={() => setSortKey(tab.value)}
                    className={`h-11 shrink-0 whitespace-nowrap border-b-2 text-sm font-medium transition-colors md:text-[0.95rem] ${
                      isSelected
                        ? "border-foreground text-foreground"
                        : "border-transparent text-[#6E6E73] hover:text-foreground dark:text-zinc-400"
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>

            <div className="flex items-center gap-2.5 md:pb-2">
              <label className="relative block flex-1 md:w-60 md:flex-none">
                <span className="sr-only">Search {categoryName} models</span>
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6E6E73]" />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={event => setSearchQuery(event.target.value)}
                  placeholder={`Search ${categoryName} models`}
                  className="h-11 w-full rounded-full border-0 bg-[#F5F5F7] pl-9 pr-3 text-[0.95rem] text-foreground placeholder:text-[#86868B] focus:outline-none focus:ring-2 focus:ring-primary/40 dark:bg-white/[0.06] md:h-9 md:text-[0.8rem]"
                />
              </label>
              <div
                role="group"
                aria-label="Layout"
                className="flex gap-0.5 rounded-full bg-[#F5F5F7] p-[3px] dark:bg-white/[0.06]"
              >
                {(
                  [
                    { value: "grid", label: "Grid view", Icon: LayoutGrid },
                    { value: "list", label: "List view", Icon: List },
                  ] as const
                ).map(option => {
                  const isSelected = view === option.value;

                  return (
                    <button
                      key={option.value}
                      type="button"
                      aria-label={option.label}
                      aria-pressed={isSelected}
                      onClick={() => setView(option.value)}
                      className={`flex h-[2.375rem] w-10 items-center justify-center rounded-full text-foreground transition-colors md:h-[1.875rem] md:w-[2.125rem] ${
                        isSelected
                          ? "bg-white shadow-[0_1px_3px_rgba(17,17,19,0.12)] dark:bg-white/[0.14]"
                          : "text-[#6E6E73] hover:text-foreground dark:text-zinc-400"
                      }`}
                    >
                      <option.Icon className="h-4 w-4" />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="mt-3 flex flex-col gap-3 md:mt-4 md:flex-row md:items-center md:justify-between">
            {releaseYearChips.length > 0 ? (
              <div
                role="group"
                aria-label="Release year"
                className="scrollbar-none -mx-4 flex items-center gap-2 overflow-x-auto px-4 [touch-action:pan-x] md:mx-0 md:flex-wrap md:px-0"
              >
                <span className="mr-1 hidden text-[0.8rem] text-[#6E6E73] dark:text-zinc-400 md:inline">
                  Released
                </span>
                {[{ value: null, label: "All" }, ...releaseYearChips].map(chip => {
                  const isSelected = chip.value === releaseYear;

                  return (
                    <button
                      key={chip.label}
                      type="button"
                      aria-pressed={isSelected}
                      onClick={() => setReleaseYear(chip.value)}
                      className={`h-9 shrink-0 rounded-full border px-3.5 text-[0.8rem] font-medium transition-colors md:h-8 ${
                        isSelected
                          ? "border-foreground bg-foreground text-background"
                          : "border-[#D2D2D7] text-foreground hover:border-foreground dark:border-white/15"
                      }`}
                    >
                      {chip.label}
                    </button>
                  );
                })}
              </div>
            ) : (
              <span />
            )}
            <p className="text-xs text-[#6E6E73] dark:text-zinc-400 md:text-[0.8rem]">
              {isLoading
                ? "Loading models…"
                : `Showing ${shownProducts.length} of ${products.length} models`}
            </p>
          </div>

          {isLoading ? (
            <div className="mt-5 grid grid-cols-2 gap-2.5 md:mt-6 md:grid-cols-3 md:gap-4 xl:grid-cols-5">
              {Array.from({ length: 10 }).map((_, index) => (
                <div
                  key={index}
                  className="h-[16.5rem] animate-pulse rounded-[1.125rem] bg-[#F5F5F7] dark:bg-white/[0.04] sm:h-[19.5rem]"
                />
              ))}
            </div>
          ) : shownProducts.length === 0 ? (
            <div className="mt-6 rounded-[1.125rem] bg-[#F5F5F7] px-6 py-12 text-center dark:bg-white/[0.04]">
              <p className="text-[0.95rem] font-medium text-foreground">
                {searchQuery.trim()
                  ? `No ${categoryName} models match “${searchQuery.trim()}”.`
                  : `No ${categoryName} models to show.`}
              </p>
              <p className="mt-1 text-sm text-[#6E6E73] dark:text-zinc-400">
                Try another model name, or clear the filters.
              </p>
            </div>
          ) : view === "grid" ? (
            <div className="mt-5 grid grid-cols-2 gap-2.5 md:mt-6 md:grid-cols-3 md:gap-4 xl:grid-cols-5">
              {shownProducts.map(product => (
                <MarketProductCard
                  key={product.id}
                  product={product}
                  subtitle={getProductCardSubtitle(product)}
                  history={historiesByProductId.get(product.id)}
                  isHistoryLoading={historiesLoading}
                  onClick={() => trackClick(product, "grid")}
                  size="compactOnMobile"
                />
              ))}
            </div>
          ) : (
            <div className="mt-5 border-t border-[#E8E8ED] dark:border-white/10 md:mt-6">
              <div className="grid h-10 grid-cols-[minmax(0,1fr)_3.5rem_auto] items-center gap-3 border-b border-[#E8E8ED] text-xs text-[#6E6E73] dark:border-white/10 dark:text-zinc-400 md:grid-cols-[minmax(0,1fr)_7.5rem_8.125rem_6.25rem_6.875rem_6.875rem_1.5rem] md:gap-5 md:px-3">
                <span>Model</span>
                <span>30 days</span>
                <span className="text-right md:hidden">Price</span>
                <span className="hidden text-right md:block">Market price</span>
                <span className="hidden text-right md:block">Change</span>
                <span className="hidden text-right md:block">Vs. retail</span>
                <span className="hidden text-right md:block">Sales, 30 days</span>
                <span className="hidden md:block" />
              </div>
              {shownProducts.map(product => {
                const change = getMarketChangeValue(product);
                const vsRetail = getVsRetail(product);
                const recentSales = recentSalesByProductId.get(product.id) ?? null;
                const released = getReleaseTimestamp(product);

                return (
                  <Link
                    key={product.id}
                    href={getProductPath(product)}
                    onClick={() => trackClick(product, "list")}
                    className="group grid min-h-[4.5rem] grid-cols-[minmax(0,1fr)_3.5rem_auto] items-center gap-3 border-b border-[#F0F0F3] py-2 text-foreground transition-colors hover:bg-[#FAFAFC] dark:border-white/[0.06] dark:hover:bg-white/[0.03] md:grid-cols-[minmax(0,1fr)_7.5rem_8.125rem_6.25rem_6.875rem_6.875rem_1.5rem] md:gap-5 md:px-3"
                  >
                    <span className="flex min-w-0 items-center gap-3 md:gap-3.5">
                      <span className="flex h-12 w-10 shrink-0 items-center justify-center md:h-[3.25rem] md:w-12">
                        {product.imageUrl ? (
                          <img
                            src={toDisplayAssetUrl(product.imageUrl)}
                            alt=""
                            loading="lazy"
                            className={`max-h-full max-w-full object-contain ${PHOTO_PLATE_CLASS} dark:p-1`}
                          />
                        ) : null}
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-semibold transition-colors group-hover:text-primary md:text-[0.95rem]">
                          {product.marketModel}
                        </span>
                        <span className="mt-0.5 block text-[0.7rem] text-[#6E6E73] dark:text-zinc-400 md:text-xs">
                          {released == null ? "" : `Released ${monthYearFormatter.format(released)}`}
                        </span>
                      </span>
                    </span>
                    <span className="flex h-6 items-center md:h-7">
                      <Sparkline
                        history={historiesByProductId.get(product.id)}
                        change={change}
                        isLoading={historiesLoading}
                        noSalesLabel="No sales"
                        fewSalesLabel="Too few sales"
                        className="h-full w-full"
                      />
                    </span>
                    <span className="flex flex-col items-end gap-0.5 md:contents">
                      <span className="font-mono text-sm font-medium md:text-right md:text-[0.95rem]">
                        {exactPriceFormatter.format(product.currentPrice)}
                      </span>
                      <span
                        className={`font-mono text-xs font-medium md:text-right md:text-sm ${
                          change == null ? "text-[#86868B]" : getChangeColorClass(change)
                        }`}
                      >
                        {change == null ? "--" : formatChange(change)}
                      </span>
                    </span>
                    <span className="hidden text-right font-mono text-sm text-[#424245] dark:text-zinc-300 md:block">
                      {vsRetail == null
                        ? "—"
                        : `${vsRetail >= 0 ? "+" : "−"}${Math.abs(vsRetail).toFixed(1)}%`}
                    </span>
                    <span className="hidden text-right font-mono text-sm text-[#424245] dark:text-zinc-300 md:block">
                      {recentSales == null ? "—" : salesCountFormatter.format(recentSales)}
                    </span>
                    <span aria-hidden="true" className="hidden text-right text-lg text-[#86868B] md:block">
                      ›
                    </span>
                  </Link>
                );
              })}
            </div>
          )}

          {!isNarrowed && !showAllModels && filteredProducts.length > PREVIEW_MODEL_COUNT ? (
            <div className="mt-6 flex justify-center md:mt-8">
              <button
                type="button"
                onClick={() => setShowAllModels(true)}
                className="h-12 w-full rounded-full border border-[#D2D2D7] px-6 text-[0.95rem] font-medium text-foreground transition-colors hover:border-foreground dark:border-white/15 md:h-11 md:w-auto"
              >
                Show all {products.length} models
              </button>
            </div>
          ) : null}
        </section>

        {/* What Moves Prices */}
        <section
          aria-labelledby="category-drivers"
          className="mt-14 bg-[#F5F5F7] dark:bg-white/[0.03] md:mt-24"
        >
          <div className="container py-12 md:py-20">
            <TwoToneHeading
              id="category-drivers"
              title={`What moves ${categoryName} prices.`}
              subtitle={
                categoryPageContent.valueDrivers.length === 3
                  ? "Three things decide what yours is worth."
                  : "Here's what decides what yours is worth."
              }
              sizeClassName="text-[1.75rem] sm:text-[2.5rem]"
            />
            <p className="mt-4 max-w-[48rem] text-[0.95rem] leading-7 text-[#424245] dark:text-zinc-300">
              {categoryPageContent.bodyIntro} {categoryPageContent.details}
            </p>
            <div className="mt-6 grid gap-3 md:mt-9 md:grid-cols-3 md:gap-4">
              {categoryPageContent.valueDrivers.map(driver => (
                <div
                  key={driver.title}
                  className="rounded-[1.125rem] bg-white p-[1.375rem] dark:bg-card dark:ring-1 dark:ring-inset dark:ring-white/[0.06] md:rounded-[1.25rem] md:p-7"
                >
                  <h3 className="text-[1.05rem] font-semibold tracking-[-0.015em] text-foreground md:text-[1.2rem]">
                    {driver.title}
                  </h3>
                  <p className="mt-2 text-sm leading-6 text-[#424245] dark:text-zinc-300 md:mt-2.5 md:text-[0.95rem]">
                    {driver.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Questions */}
        {faqItems.length > 0 ? (
          <section aria-labelledby="category-faq" className="container pt-12 md:pt-24">
            <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] lg:gap-12">
              <TwoToneHeading
                id="category-faq"
                title="Questions."
                subtitle={`About ${categoryName} value.`}
                sizeClassName="text-[1.75rem] sm:text-[2.5rem]"
              />
              <div className="border-t border-[#E8E8ED] dark:border-white/10">
                {faqItems.map((item, index) => (
                  <details
                    key={item.question}
                    open={index === 0}
                    className="group border-b border-[#E8E8ED] dark:border-white/10"
                  >
                    <summary className="flex min-h-[3.75rem] cursor-pointer list-none items-center justify-between gap-4 py-3.5 text-base font-medium text-foreground marker:content-none md:py-5 md:text-[1.05rem] [&::-webkit-details-marker]:hidden">
                      {item.question}
                      <span
                        aria-hidden="true"
                        className="shrink-0 text-2xl font-light leading-none text-[#6E6E73] transition-transform duration-200 group-open:rotate-45"
                      >
                        +
                      </span>
                    </summary>
                    <p className="max-w-[42rem] pb-5 text-sm leading-6 text-[#424245] dark:text-zinc-300 md:text-[0.95rem] md:leading-7">
                      {item.answer}
                    </p>
                  </details>
                ))}
              </div>
            </div>
          </section>
        ) : null}

        {/* Other Markets */}
        <nav aria-labelledby="category-related" className="container pb-16 pt-12 md:pb-24 md:pt-24">
          <h2
            id="category-related"
            className="text-[1.375rem] font-semibold tracking-[-0.02em] text-foreground md:text-[1.75rem]"
          >
            Explore other markets
          </h2>
          <div className="scrollbar-none -mx-4 mt-3 overflow-x-auto px-4 [touch-action:pan-x] md:mx-0 md:mt-5 md:px-0">
            <div className="flex w-max gap-1 md:gap-2">
              {relatedMarkets.map(market => (
                <Link
                  key={market.slug}
                  href={getCatalogFilterPath(market.slug)}
                  className="group flex w-[5.75rem] flex-col items-center gap-2.5 px-1 py-2.5 md:w-[7.75rem] md:py-4"
                >
                  <span className="flex h-14 w-full items-end justify-center md:h-[4.5rem]">
                    {market.imageUrl ? (
                      <img
                        src={market.imageUrl}
                        alt=""
                        loading="lazy"
                        className={`max-h-14 max-w-[4.5rem] object-contain md:max-h-[4.5rem] md:max-w-[5.75rem] ${PHOTO_PLATE_CLASS} dark:p-1.5`}
                      />
                    ) : null}
                  </span>
                  <span className="text-xs font-medium text-foreground transition-colors group-hover:text-primary md:text-[0.8rem]">
                    {market.title}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </nav>
      </main>

      <div className="mt-auto">
        <Footer />
      </div>
    </div>
  );
}
